import React, { Component } from "react";
import { Grid } from 'semantic-ui-react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import Input from 'components/final-form/Input';
import { debounce } from 'ts-debounce';
import { DosageDto, DosageSearchRequest, UpsertDosageDto } from 'ts-types/api.types';
import { FormState } from 'final-form';
import { addDosage, deleteDosage, editDosage, searchDosages } from 'services/dosageServices';
import axios, { CancelTokenSource } from 'axios';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import CheckBox from 'components/final-form/CheckBox';
import { required } from 'utils/validatorUtils';
import { noop } from 'utils/functionUtils';
import LoaderComponent from 'components/LoaderComponent';
import DeleteActionButton from 'components/DeleteActionButton';
import PxGrid from 'components/layout/PxGrid';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import EditActionButton from 'components/EditActionButton';
import AddButton from 'components/AddButton';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import SaveActionButton from 'components/SaveActionButton';
import CancelActionButton from 'components/CancelActionButton';
import SearchResultComponent from 'components/SearchResultComponent';

const initialDosageSearch: Partial<DosageSearchRequest> = {};

interface DosageFieldProps extends FormState {
  values: Partial<DosageSearchRequest>
}

interface Props extends MessageSourceProps {
}

interface State {
  foundDosages: Array<DosageDto>,
  dosagesLoaded: boolean,
  upsertDosage?: UpsertDosageDto,
  upsertActive: boolean,
  cancelTokenSource: CancelTokenSource,
  searchValues: Partial<DosageSearchRequest>,
  errorMessage?: string
}

class DosagesView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      foundDosages: [],
      dosagesLoaded: false,
      upsertDosage: undefined,
      upsertActive: false,
      searchValues: initialDosageSearch,
      cancelTokenSource
    };

  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel('Operation canceled by user');
  }

  newDosage = () => {

    const defaultValues: DosageDto = {
      id: -1,
      description: "",
      mark: 0,
      active: true
    };

    this.setState(({foundDosages, upsertActive}: State) => ({
      foundDosages: [defaultValues, ...foundDosages],
      upsertDosage: defaultValues,
      upsertActive: !upsertActive
    }))

  };

  editDosage = (id: number) => {

    const {foundDosages} = this.state;
    const dosage = foundDosages.find((dos) => {
      return dos.id == id
    });

    this.setState(({upsertActive}: State) => ({
      upsertActive: !upsertActive,
      upsertDosage: dosage
    }));
  };

  searchAfterSubmit = () => {
    const {searchValues, cancelTokenSource} = this.state;

    this.setState(
        {dosagesLoaded: false}
    );

    const onFinally = () => {
      this.setState({
        dosagesLoaded: true
      })
    };

    searchDosages(searchValues, cancelTokenSource)
        .then(response => {
          this.setState({
            foundDosages: response,
            upsertActive: false
          });
        })
        .catch(noop)
        .finally(onFinally);
  };

  handleSubmit = (values: Partial<UpsertDosageDto>) => {

    const {upsertDosage, cancelTokenSource} = this.state;


    let saveValues: Partial<UpsertDosageDto> = {...values};

    if (values.mark) {
      saveValues.mark = 1;
    } else {
      saveValues.mark = 0;
    }

    const {getMessage} = this.props;

    const handleError = () => {
      this.setState({
        errorMessage: getMessage("error.general")
      })
    };

    if (upsertDosage && upsertDosage.id != -1) {
      editDosage(upsertDosage.id, saveValues, cancelTokenSource)
          .then(this.searchAfterSubmit)
          .catch(handleError)
    } else {
      addDosage(saveValues, cancelTokenSource)
          .then(this.searchAfterSubmit)
          .catch(handleError);
    }

  };

  onCancel = () => {
    this.setState(({foundDosages, upsertActive}: State) => {
      const dosages = foundDosages.filter(dosage => dosage.id != -1);
      return ({
        foundDosages: [...dosages],
        upsertDosage: undefined,
        upsertActive: !upsertActive
      })
    });
  };

  deleteDosage = (id: number): void => {
    const {cancelTokenSource} = this.state;
    deleteDosage(id, cancelTokenSource).then(response => {
      this.setState(({foundDosages}: State) => ({
        foundDosages: foundDosages.filter((dosage: DosageDto) => dosage.id != response.id)
      }))
    }).catch(noop);
  };

  handleDosageChange = debounce(({values}: DosageFieldProps): void => {
    const {cancelTokenSource} = this.state;
    this.setState({dosagesLoaded: false});
    if (!this.mounted) {
      return;
    }

    const onFinally = () => {
      this.setState({
        dosagesLoaded: true
      })
    };

    searchDosages(values, cancelTokenSource)
        .then(response => {
          this.setState({
            foundDosages: response,
            searchValues: values
          });
        })
        .catch(noop)
        .finally(onFinally);
  }, 300);

  render(): React.ReactNode {
    const {dosagesLoaded} = this.state;
    const {getMessage} = this.props;
    const dosageView = (
        <PraxStyledSegment>
          {this.renderSearchDosageSegment()}

          <StyledViewTableRow>
            {!dosagesLoaded ? <LoaderComponent message={getMessage("dosage.loading")}/> : this.renderDosagesSegment()}
          </StyledViewTableRow>

        </PraxStyledSegment>
    );
    return (
        <React.Fragment>
          {dosageView}
        </React.Fragment>
    );
  }

  renderSearchDosageSegment = (): JSX.Element => {
    return (
        <FinalForm
            onSubmit={() => {
            }}
            subscription={{pristine: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {upsertActive, foundDosages} = this.state;

    const numResults = foundDosages.length;

    return (
        <PxGrid>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("description")}:
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="description"
                  component={Input}
                  fluid
                  autoFocus
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <AddButton buttonMessage={getMessage("button.add")} disabled={upsertActive}
                         onClick={() => this.newDosage()}/>
            </Grid.Column>
          </Grid.Row>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true}} onChange={this.handleDosageChange}/>
        </PxGrid>
    )
  };

  descriptionCellRenderer = ({rowData, cellData, dataKey}: any) => {
    const {upsertActive, upsertDosage} = this.state;
    const dosageId = rowData.id;

    if (upsertDosage && dosageId == upsertDosage.id && upsertActive) {
      const validator = (value: string) => required(value);
      return (
          <React.Fragment>
            <Field
                name={dataKey}
                component={Input}
                validate={validator}
                autoFocus
            />
          </React.Fragment>
      );
    }

    return (
        <React.Fragment>
          {cellData}
        </React.Fragment>
    );
  };

  markCellRenderer = ({rowData, cellData, dataKey}: any) => {
    const {getMessage} = this.props;
    const {upsertActive, upsertDosage} = this.state;
    const dosageId = rowData.id;

    if (upsertDosage && dosageId == upsertDosage.id && upsertActive) {
      return (
          <React.Fragment>
            <Field
                name={dataKey}
                component={CheckBox}
            />
          </React.Fragment>
      );
    }

    return (
        <React.Fragment>
          {cellData ? getMessage("dosage.warn") : null}
        </React.Fragment>
    );
  };

  activeCellRenderer = ({rowData, cellData, dataKey}: any) => {
    const {getMessage} = this.props;
    const {upsertActive, upsertDosage} = this.state;
    const dosageId = rowData.id;

    if (upsertDosage && dosageId == upsertDosage.id && upsertActive) {
      return (
          <React.Fragment>
            <Field
                name={dataKey}
                component={CheckBox}
            />
          </React.Fragment>
      );
    }

    return (
        <React.Fragment>
          {cellData && getMessage("dosage.active")}
        </React.Fragment>
    );
  };

  actionsCellRenderer = ({rowData, cellData}: any) => {
    const {getMessage} = this.props;
    const {upsertActive, upsertDosage} = this.state;
    const dosageId = rowData.id;

    if (upsertDosage && dosageId == upsertDosage.id && upsertActive) {
      return (
          <React.Fragment>
            <React.Fragment>
              <SaveActionButton hoverMessage={getMessage('button.save')}/>
              <CancelActionButton hoverMessage={getMessage('button.cancel')} onConfirm={() => this.onCancel()}/>
            </React.Fragment>
          </React.Fragment>
      );
    }

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage('msgEdit')}
              onConfirm={() => this.editDosage(cellData)}
              disabled={upsertActive}
          />
          <DeleteActionButton
              hoverMessage={getMessage('msgDelete')}
              popupMessage={getMessage("dosage.confirmDelete")}
              onConfirm={() => this.deleteDosage(cellData)}
              disabled={upsertActive}
          />
        </React.Fragment>
    );
  };

  rowGetter = ({index}: any) => {
    const {foundDosages} = this.state;

    Object.assign(foundDosages[index], {index: index + 1});

    return foundDosages[index];
  };

  foundDosagesRowRenderer = ({className, columns, index, key, style, rowData}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    const {upsertDosage, upsertActive} = this.state;
    const dosageId = rowData.id;

    if (upsertDosage && dosageId == upsertDosage.id && upsertActive) {

      const styleCopy = style;

      style = {
        position: "relative"
      };

      return (
          <div style={style}
               key={key}
          >
            <FinalForm
                onSubmit={(values) => this.handleSubmit(values)}
                initialValues={upsertDosage}
                subscription={{values: true, pristine: true}}
                render={({handleSubmit}) => (
                    <form onSubmit={handleSubmit}
                          {...a11yProps}
                          className={className}
                          role="row"
                          style={styleCopy}
                    >

                      {columns}
                    </form>
                )}
            />
          </div>
      );
    }

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
        >
          {columns}
        </div>
    );
  };

  renderDosagesSegment = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {foundDosages} = this.state;

    return (
        <VirtualizedTable
            rowCount={foundDosages.length}
            rowGetter={this.rowGetter}
            rowRenderer={this.foundDosagesRowRenderer}
            rowHeight={42}
            scrollToIndex={0}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "index"
              },
              {
                width: 300,
                flexGrow: 1,
                label: (getMessage("description")),
                dataKey: "description",
                cellRenderer: (this.descriptionCellRenderer)
              },
              {
                width: 400,
                label: (getMessage("dosage.warn")),
                dataKey: "mark",
                cellRenderer: (this.markCellRenderer)
              },
              {
                width: 300,
                flexGrow: 1,
                label: (getMessage("dosage.active")),
                dataKey: "active",
                cellRenderer: (this.activeCellRenderer)
              },
              {
                width: 100,
                label: (getMessage("actions")),
                dataKey: "id",
                cellRenderer: (this.actionsCellRenderer)
              }
            ]}
        />
    );
  };

}

export default withMessages(DosagesView);
